import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCog, Search, X, Loader2 } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import API_ENDPOINTS from '../api/endpoints';
import { startSpoof, exitSpoof } from '../store/slices/authSlice';
import { isSuperAdmin } from '../utils/roles';
import toast from 'react-hot-toast';
import styles from './SpoofUI.module.css';

const SpoofBanner = () => {
  const dispatch = useDispatch();
  const spoofingAs = useSelector((state) => state.auth.spoofingAs);

  if (!spoofingAs) return null;

  return (
    <div className={styles.spoofBanner}>
      <span className={styles.spoofBannerText}>
        Viewing CRM as <strong>{spoofingAs.userName}</strong> ({spoofingAs.userCode})
      </span>
      <button
        type="button"
        className={styles.spoofExitBtn}
        onClick={() => dispatch(exitSpoof())}
      >
        <X size={16} />
        Exit spoof
      </button>
    </div>
  );
};

const SpoofDropdown = ({ onClose }) => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setUsers([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      axiosInstance
        .get(API_ENDPOINTS.spoofSearch, { params: { q: query.trim() } })
        .then((res) => {
          const data = res.data?.response?.[0]?.data;
          setUsers(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          setUsers([]);
          toast.error('Failed to search users');
        })
        .finally(() => {
          setLoading(false);
        });
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const handleSpoof = async (userCode) => {
    setSubmitting(true);
    try {
      await dispatch(startSpoof({ targetUserCode: userCode })).unwrap();
      toast.success('Spoof started');
      onClose?.();
    } catch (err) {
      toast.error(err || 'Spoof failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.spoofDropdown}>
      <div className={styles.spoofSearchWrap}>
        <Search size={18} className={styles.spoofSearchIcon} />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search by name or code (min 2 chars)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={styles.spoofSearchInput}
        />
        {loading && <Loader2 size={18} className={styles.spoofSpinner} />}
      </div>
      <div className={styles.spoofUserList}>
        {users.length === 0 && query.trim().length >= 2 && !loading && (
          <div className={styles.spoofEmpty}>No users found</div>
        )}
        {users.map((u) => (
          <button
            key={u._id}
            type="button"
            className={styles.spoofUserItem}
            onClick={() => handleSpoof(u.userCode)}
            disabled={submitting}
          >
            <div className={styles.spoofUserMain}>
              <span className={styles.spoofUserName}>{u.userName}</span>
              <span className={styles.spoofUserCode}>{u.userCode}</span>
            </div>
            {u.orgName && (
              <span className={styles.spoofUserOrg}>{u.orgName}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const SpoofTrigger = () => {
  const user = useSelector((state) => state.auth.user);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (!isSuperAdmin(user)) return null;

  return (
    <div className={styles.spoofTriggerWrap} ref={containerRef}>
      <button
        type="button"
        className={styles.spoofTrigger}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <UserCog size={18} />
        <span>Spoof</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className={styles.spoofDropdownWrap}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            <SpoofDropdown onClose={() => setOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export { SpoofBanner, SpoofTrigger };
